import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import I18n from 'utils/I18n';
import ActionsCameraNormalSmall from '@strava/icons/ActionsCameraNormalSmall';
import { devLog } from 'utils/devUtils';
import styles from './MediaUploader.scss';
import Thumbnail from './Thumbnail';
import { getPhotoWithMaxDimension } from '../../utils/generalUtils';
import {
  getThumbnailPreviewUrl,
  getMediaType,
  getUuid,
  normalizeId,
  normalizeMediaType,
  getMediaUrl,
  storeMedia
} from './MediaUploader.utils';

function MediaUploader({ athleteId, media, defaultPhotoId }) {
  const [files, setFiles] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [highlightMediaId, setHighlightMediaId] = useState(defaultPhotoId);

  async function processFile(file) {
    const uuid = getUuid();
    const mediaType = getMediaType(file);
    const mediaUrl = await getMediaUrl(file, uuid, athleteId);
    const previewUrl = await getThumbnailPreviewUrl(file);

    // Instantiate the loading state of the file
    // for the circular progress bar
    setLoadingStates((prevState) => ({
      ...prevState,
      [uuid]: 0
    }));

    setFiles((prevState) => {
      return [
        ...prevState,
        ...Object.assign(file, {
          preview: previewUrl,
          uuid,
          mediaType
        })
      ];
    });

    // Store the media to s3 and update the progress bar live
    await storeMedia(mediaUrl.data, file, (progress) => {
      setLoadingStates((prevState) => ({
        ...prevState,
        [uuid]: progress
      }));
    });
  }

  // Dropzone
  const { getRootProps, getInputProps } = useDropzone({
    accept: ['image/*', 'video/*'],
    onDrop: (acceptedFiles) => {
      acceptedFiles.forEach(async (file) => {
        processFile(file);
      });
    },
    maxSize: 100000000, // 100 mb
    onError: (error) => {
      // TODO: Make a better error handling
      devLog(error.message);
    }
  });

  const setHighlightMedia = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    devLog(index);
    const newId = normalizeId(files[index]);
    setHighlightMediaId(newId);
  };

  const remove = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const newFiles = [...files];
    const newId = normalizeId(newFiles[index]);
    if (highlightMediaId === newId) {
      setHighlightMediaId(normalizeId(newFiles[0]));
    }
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const thumbnails = files.map((file, index) => {
    return (
      <Thumbnail
        file={file}
        index={index}
        remove={remove}
        loadingStates={loadingStates}
        uuid={normalizeId(file)}
        highlightMediaId={highlightMediaId}
        setHighlightMedia={setHighlightMedia}
        mediaType={normalizeMediaType(file)}
      />
    );
  });

  useEffect(() => {
    // Load previews of media that are already attached to activity
    setFiles([
      ...media.map((file) => {
        setLoadingStates((prevState) => ({
          ...prevState,
          [normalizeId(file)]: 100
        }));
        return Object.assign(file, {
          preview: getPhotoWithMaxDimension(file.urls)
        });
      })
    ]);

    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  return (
    <div>
      {/* TODO: Change this to whatever we want it called in new design */}
      <div className={styles.label}>Media</div>
      <div {...getRootProps({ className: styles.dropzone })}>
        <input {...getInputProps()} />
        {files.length === 0 && (
          <>
            {/* TODO: Change this to whatever we want it in new design - Placeholder */}
            <ActionsCameraNormalSmall fill="#b2afc0" />
            <p className={styles.header}>
              {I18n.t(`templates.photos.form.dropzone_header`)}
            </p>
            <p className={styles.subheader}>
              {I18n.t(`templates.photos.form.dropzone_sub`)}
            </p>
          </>
        )}
        {thumbnails.length > 0 && (
          <>
            <div className={styles.mediaContainer}>{thumbnails}</div>
            {/* Needed to pass value to update activity call */}
            <input
              type="hidden"
              name="default_photo_id"
              value={highlightMediaId}
            />
          </>
        )}
      </div>
    </div>
  );
}

MediaUploader.propTypes = {
  athleteId: PropTypes.number.isRequired,
  media: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultPhotoId: PropTypes.number.isRequired
};

export default MediaUploader;
